import type { z } from 'zod';

/** What `useAppForm`'s `validators.onChange` expects back: per-field messages, or nothing when valid. */
type FieldErrors = { fields: Record<string, string> } | undefined;

/**
 * The `useAppForm` field name for an issue path, in TanStack's path syntax.
 *
 * - Whole path, not the first segment, or a nested field goes invalid with no message.
 * - `undefined` for an empty (form-level) path.
 */
const fieldName = (path: readonly PropertyKey[]): string | undefined => {
  let name = '';
  for (const step of path) {
    if (typeof step === 'number') name += `[${step}]`;
    else if (typeof step === 'string') name += name === '' ? step : `.${step}`;
    else return undefined;
  }

  return name === '' ? undefined : name;
};

/**
 * Adapts a zod schema into a `useAppForm` validator with one message per field.
 *
 * - Passing the schema directly stacks every issue a field collected.
 * - Keeps the first issue per field; `.refine` issues land on their `path` field.
 * - Form-level issues (empty path) are dropped — use `setSubmitError`.
 */
export const zodValidator =
  <Schema extends z.ZodType>(schema: Schema) =>
  ({ value }: { value: unknown }): FieldErrors => {
    const result = schema.safeParse(value);
    if (result.success) return undefined;

    const fields: Record<string, string> = {};
    for (const issue of result.error.issues) {
      const field = fieldName(issue.path);
      if (field === undefined) continue;
      fields[field] ??= issue.message;
    }

    return Object.keys(fields).length > 0 ? { fields } : undefined;
  };

/**
 * The app's validation timing: `onChange` only.
 *
 * - `FieldItem` reveals errors after blur or submit; submit already runs `onChange`.
 * - Adding `onBlur` leaves a stale "Required": each cause clears only its own slot.
 * - `schema` may be a function of the values, for forms whose schema depends on them.
 */
export const formValidators = <Schema extends z.ZodType, Value = unknown>(
  schema: Schema | ((value: Value) => Schema),
) => ({
  onChange: ({ value }: { value: Value }): FieldErrors =>
    zodValidator(typeof schema === 'function' ? schema(value) : schema)({ value }),
});
